import React, { useState } from 'react';
import { BaseModal } from '../../UI/Modal/BaseModal';
import { Input } from '../../UI/V3/Forms/Input/Input';
import { Checkbox } from '../../UI/V3/Forms/Checkbox/Checkbox';
import Button from '../../UI/V3/Button/Button';
import { Organization } from 'model/organization';
import { SaveLocation, BusinessAreaType, Location } from '../../../model/location';
import styles from './AddLocationModal.module.scss';
import { useAuth } from 'hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import { validatePhoneNumber } from '@spike/validations';
import PhoneField3 from 'components/UI/PhoneField3';
import TreeSelect from '../../common/TreeSelect/TreeSelect';

interface LocationFormData {
    organizationId: string;
    businessName: string;
    email: string;
    phone: string;
    address: string;
    addressId?: number;
    suite: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
    sameAsOrganization: boolean;
    businessAreas: BusinessAreaType[];
    termsOfService: boolean;
    privacyPolicy: boolean;
    // Hidden fields from account (populated when checkbox is checked)
    nicknameDba?: string;
    taxId?: string;
    businessType?: string;
}

type ModalMode = 'add' | 'edit' | 'view';

interface AddLocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave?: (locationData: SaveLocation) => Promise<void>;
    accountId?: string; // When provided, the account select will be disabled
    account?: Organization; // The selected account (for pre-filling when "Same as organization" is checked)
    accountOptions: Array<{ label: string; value: string }>;
    location?: Location | null; // When provided, the modal is in edit or view mode
    mode?: ModalMode; // 'add' | 'edit' | 'view' - defaults to 'add' or 'edit' based on location prop
}

export const AddLocationModal: React.FC<AddLocationModalProps> = ({
    isOpen,
    onClose,
    onSave,
    accountId,
    account,
    accountOptions,
    location,
    mode: modeProp
}) => {
    const { businessAreas } = useSelector((state: RootState) => state.masterData.data);
    const organizations = useSelector((state: RootState) => state.organizations.organizations);
    const isSaving = useSelector((state: RootState) => state.locations.loading);

    // Get parent organization (where parentOrganizationId is null)
    const parentOrganization = organizations.find(
        (org: any) => org.parentOrganizationId === null || org.parentOrganizationId === undefined
    );
    const organizationCountry = parentOrganization?.country || 'US';

    // Determine the actual mode
    const mode: ModalMode = modeProp || (location ? 'edit' : 'add');
    const isViewMode = mode === 'view';
    const [showExitPrompt, setShowExitPrompt] = useState(false);
    const [formData, setFormData] = useState<LocationFormData>({
        businessName: '',
        organizationId: '',
        email: '',
        phone: '',
        address: '',
        addressId: undefined,
        suite: '',
        city: '',
        state: '',
        zipcode: '',
        country: '',
        sameAsOrganization: false,
        businessAreas: [],
        termsOfService: false,
        privacyPolicy: false,
        nicknameDba: '',
        taxId: '',
        businessType: ''
    });

    const [errors, setErrors] = useState<Partial<LocationFormData>>({});
    const auth = useAuth();
    const userId = auth.user?.id;

    // Pre-populate form when modal opens
    React.useEffect(() => {
        if (isOpen) {
            if (location) {
                // Edit mode - populate with location data
                // Extract address from addresses_attributes if available
                const addressData = location.addressesAttributes?.[0];

                setFormData({
                    organizationId: location.organizationId.toString(),
                    businessName: location.businessName,
                    email: location.email,
                    phone: location.phone,
                    address: addressData?.addressLineOne || '',
                    addressId: addressData?.id,
                    suite: addressData?.addressLineTwo || '',
                    city: addressData?.city || '',
                    state: addressData?.state || '',
                    zipcode: addressData?.zipcode || '',
                    country: addressData?.country || '',
                    sameAsOrganization: false,
                    businessAreas: location.businessAreas || [],
                    termsOfService: true, // Assume already agreed for existing locations
                    privacyPolicy: true, // Assume already agreed for existing locations
                    nicknameDba: '',
                    taxId: '',
                    businessType: ''
                });
            } else if (accountId) {
                // Add mode with pre-selected account
                setFormData(prev => ({
                    ...prev,
                    organizationId: accountId
                }));
            }
        }
    }, [isOpen, accountId, location]);

    const handleInputChange = (field: keyof LocationFormData, value: string | boolean) => {
        // Handle checkbox toggle
        if (field === 'sameAsOrganization' && typeof value === 'boolean') {
            if (value && account) {
                // Fill form with organization data
                setFormData(prev => ({
                    ...prev,
                    sameAsOrganization: true,
                    email: account.email || '',
                    phone: account.phone || '',
                    address: account.addressLineOne || '',
                    suite: account.addressLineTwo || '',
                    city: account.city || '',
                    state: account.state || '',
                    zipcode: account.zipcode || '',
                    country: account.country || '',
                    // Hidden fields from account
                    nicknameDba: account.nicknameDba || '',
                    taxId: account.taxId || '',
                    businessType: account.businessType || ''
                }));
            } else {
                // Clear the fields when unchecked
                setFormData(prev => ({
                    ...prev,
                    sameAsOrganization: false,
                    email: '',
                    phone: '',
                    address: '',
                    suite: '',
                    city: '',
                    state: '',
                    zipcode: '',
                    country: '',
                    // Clear hidden fields
                    nicknameDba: '',
                    taxId: '',
                    businessType: ''
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));

            // Validate phone field as user types
            if (field === 'phone' && typeof value === 'string') {
                validatePhoneField(value);
                return; // Don't clear error - let validation handle it
            }
        }

        // Clear error when user starts typing (except for phone)
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleBusinessAreaToggle = (area: BusinessAreaType) => {
        setFormData(prev => {
            const currentAreas = prev.businessAreas || [];
            const newAreas = currentAreas.includes(area)
                ? currentAreas.filter(a => a !== area)
                : [...currentAreas, area];

            return {
                ...prev,
                businessAreas: newAreas
            };
        });

        // Clear error when user selects an option
        if (errors.businessAreas) {
            setErrors(prev => ({
                ...prev,
                businessAreas: undefined
            }));
        }
    };

    const validatePhoneField = (phoneValue: string) => {
        if (formData.sameAsOrganization) return;

        let phoneError: string | undefined = undefined;

        if (!phoneValue?.trim()) {
            phoneError = 'Phone is required';
        } else if (phoneValue) {
            const validationErrors = validatePhoneNumber(phoneValue, organizationCountry, 'phone');
            if (validationErrors.length > 0) {
                phoneError = validationErrors[0].errorMessage;
            }
        }

        setErrors(prev => ({
            ...prev,
            phone: phoneError
        }));
    };

    const handlePhoneBlur = () => {
        validatePhoneField(formData.phone || '');
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<LocationFormData> = {};

        // Required fields validation
        if (!formData.organizationId?.trim()) {
            newErrors.organizationId = 'Account is required';
        }
        if (!formData.businessName?.trim()) {
            newErrors.businessName = 'Location name is required';
        }
        if (!formData.businessAreas || formData.businessAreas.length === 0) {
            (newErrors as Record<string, string>).businessAreas = 'Please select at least one business area';
        }
        if (!formData.termsOfService) {
            (newErrors as Record<string, string | boolean>).termsOfService = 'You must agree to the Terms of Service';
        }
        if (!formData.privacyPolicy) {
            (newErrors as Record<string, string | boolean>).privacyPolicy = 'You must agree to the Privacy Policy';
        }
        if (!formData.sameAsOrganization) {
            if (!formData.email?.trim()) {
                newErrors.email = 'Work email is required';
            }
            if (!formData.address?.trim()) {
                newErrors.address = 'Address line 1 is required';
            }
            if (!formData.city?.trim()) {
                newErrors.city = 'City is required';
            }
            if (!formData.state?.trim()) {
                newErrors.state = 'State is required';
            }
            if (!formData.zipcode?.trim()) {
                newErrors.zipcode = 'Zipcode is required';
            }
            if (!formData.country?.trim()) {
                newErrors.country = 'Country is required';
            }

            // Email validation
            const emailRegex = /\S+@\S+\.\S+/;
            if (formData.email && !emailRegex.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
            }

            if (!formData.phone?.trim()) {
                newErrors.phone = 'Phone number is required';
            } else if (formData.phone) {
                const phoneErrors = validatePhoneNumber(formData.phone, organizationCountry, 'phoneNumber');
                if (phoneErrors.length > 0) {
                    newErrors.phone = phoneErrors[0].errorMessage;
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (validateForm()) {
            // Build addresses_attributes from form data
            const addresses_attributes =
                formData.address || formData.city || formData.state || formData.zipcode || formData.country
                    ? [
                          {
                              marketplaceId: location?.id || 0, // Will be set after POST in the thunk
                              id: formData.addressId,
                              addressLineOne: formData.address,
                              addressLineTwo: formData.suite,
                              city: formData.city,
                              state: formData.state,
                              zipcode: formData.zipcode,
                              country: formData.country
                          }
                      ]
                    : undefined;

            // Map LocationFormData to SaveLocation
            const locationData: SaveLocation = {
                userId: userId!,
                organizationId: parseInt(formData.organizationId),
                email: formData.email,
                businessName: formData.businessName,
                businessAreas: formData.businessAreas,
                phone: formData.phone,
                // Keep individual fields for backward compatibility
                address: formData.address,
                suite: formData.suite,
                city: formData.city,
                state: formData.state,
                zipcode: formData.zipcode,
                country: formData.country,
                termsOfService: formData.termsOfService,
                privacyPolicy: formData.privacyPolicy,
                // Add addresses_attributes for new API flow
                addressesAttributes: addresses_attributes,
                // Include id and active status when editing
                ...(location && {
                    id: location.id,
                    active: location.active
                })
            };

            //console.log(locationData)

            // Wait for the save operation to complete before closing
            await onSave?.(locationData);
        }
    };

    const handleClose = () => {
        // In view mode, close directly without showing exit prompt
        if (isViewMode) {
            resetFormAndClose();
        } else {
            setShowExitPrompt(true);
        }
    };

    const resetFormAndClose = () => {
        // Reset form when closing
        setFormData({
            businessName: '',
            organizationId: '',
            email: '',
            phone: '',
            address: '',
            suite: '',
            city: '',
            state: '',
            zipcode: '',
            country: '',
            sameAsOrganization: false,
            businessAreas: [],
            termsOfService: false,
            privacyPolicy: false,
            nicknameDba: '',
            taxId: '',
            businessType: ''
        });
        setErrors({});
        setShowExitPrompt(false);
        onClose();
    };

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={handleClose}
                className={styles.addLocationModal}
            >
                <BaseModal.Header
                    showLogo={true}
                    showCloseButton={true}
                />

                <BaseModal.Content className={styles.modalContent}>
                    <div className={styles.formContainer}>
                        <form
                            className={styles.form}
                            onSubmit={e => e.preventDefault()}
                        >
                            <div className={styles.locationSection}>
                                <h2 className={styles.modalTitle}>
                                    {mode === 'view'
                                        ? 'Location details'
                                        : mode === 'edit'
                                        ? 'Edit location'
                                        : 'Add a location'}
                                </h2>

                                {/* Account Selection */}
                                <div className={styles.formField}>
                                    <TreeSelect
                                        label="Organization"
                                        options={accountOptions}
                                        value={formData.organizationId}
                                        onChange={value => handleInputChange('organizationId', value)}
                                        placeholder="Select an organization"
                                        error={errors.organizationId}
                                        disabled={isViewMode}
                                        required
                                    />
                                </div>

                                {/* Same as organization checkbox - only show when account is selected and not in view mode */}
                                {account && !isViewMode && (
                                    <div className={styles.checkboxField}>
                                        <Checkbox
                                            label="Same as organization details"
                                            checked={formData.sameAsOrganization}
                                            onChange={checked => handleInputChange('sameAsOrganization', checked)}
                                        />
                                    </div>
                                )}

                                {/* Location name */}
                                <div className={styles.formField}>
                                    <Input
                                        id="businessName"
                                        label="Location name"
                                        value={formData.businessName}
                                        onChange={e => handleInputChange('businessName', e.target.value)}
                                        placeholder="Enter location name"
                                        error={errors.businessName}
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* Business areas */}
                                <div className={styles.formField}>
                                    <label className={styles.fieldLabel}>Business areas</label>
                                    <div
                                        className={`${styles.checkboxGroup} ${styles.businessAreasGroup}`}
                                        style={isViewMode ? { pointerEvents: 'none', opacity: 0.7 } : {}}
                                    >
                                        {businessAreas.map((area: { id: string; name: string }) => (
                                            <Checkbox
                                                key={area.id}
                                                label={area.name}
                                                checked={formData.businessAreas?.includes(
                                                    area.name.toLowerCase() as BusinessAreaType
                                                )}
                                                onChange={() =>
                                                    handleBusinessAreaToggle(
                                                        area.name.toLowerCase() as BusinessAreaType
                                                    )
                                                }
                                            />
                                        ))}
                                    </div>
                                    {errors.businessAreas && typeof errors.businessAreas === 'string' && (
                                        <span className={styles.errorText}>{errors.businessAreas}</span>
                                    )}
                                </div>

                                {/* Location Work Email and Phone Number Row */}
                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <Input
                                            id="email"
                                            label="Work email"
                                            type="email"
                                            value={formData.email}
                                            onChange={e => handleInputChange('email', e.target.value)}
                                            placeholder="Enter work email"
                                            error={errors.email}
                                            disabled={isViewMode || formData.sameAsOrganization}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <PhoneField3
                                            id="phone"
                                            name="phone"
                                            label="Phone number"
                                            value={formData.phone}
                                            onChange={value => handleInputChange('phone', value)}
                                            onBlur={handlePhoneBlur}
                                            placeholder="(___) ___-____"
                                            errors={
                                                errors.phone
                                                    ? [{ fieldName: 'phone', errorMessage: errors.phone }]
                                                    : undefined
                                            }
                                            disabled={isViewMode || formData.sameAsOrganization}
                                        />
                                    </div>
                                </div>

                                {/* Address Line 1 */}
                                <div className={styles.formField}>
                                    <Input
                                        id="address"
                                        label="Address line 1"
                                        value={formData.address}
                                        onChange={e => handleInputChange('address', e.target.value)}
                                        placeholder="Enter address line 1"
                                        error={errors.address}
                                        disabled={isViewMode || formData.sameAsOrganization}
                                    />
                                </div>

                                {/* Address Line 2 */}
                                <div className={styles.formField}>
                                    <Input
                                        id="suite"
                                        label="Address line 2"
                                        value={formData.suite}
                                        onChange={e => handleInputChange('suite', e.target.value)}
                                        placeholder="Enter address line 2 (optional)"
                                        error={errors.suite}
                                        disabled={isViewMode || formData.sameAsOrganization}
                                    />
                                </div>

                                {/* City, State, Zipcode, Country Row */}
                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <Input
                                            id="city"
                                            label="City"
                                            value={formData.city}
                                            onChange={e => handleInputChange('city', e.target.value)}
                                            placeholder="City"
                                            error={errors.city}
                                            disabled={isViewMode || formData.sameAsOrganization}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <Input
                                            id="state"
                                            label="State"
                                            value={formData.state}
                                            onChange={e => handleInputChange('state', e.target.value)}
                                            placeholder="State"
                                            error={errors.state}
                                            disabled={isViewMode || formData.sameAsOrganization}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <Input
                                            id="zipcode"
                                            label="Zipcode"
                                            value={formData.zipcode}
                                            onChange={e => handleInputChange('zipcode', e.target.value)}
                                            placeholder="Zipcode"
                                            error={errors.zipcode}
                                            disabled={isViewMode || formData.sameAsOrganization}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <Input
                                            id="country"
                                            label="Country"
                                            value={formData.country}
                                            onChange={e => handleInputChange('country', e.target.value)}
                                            placeholder="Country"
                                            error={errors.country}
                                            disabled={isViewMode || formData.sameAsOrganization}
                                        />
                                    </div>
                                </div>

                                {/* Terms and Privacy Checkboxes - hide in view mode */}
                                {!isViewMode && (
                                    <div className={`${styles.checkboxGroup} ${styles.termsGroup}`}>
                                        <div className={styles.checkboxField}>
                                            <Checkbox
                                                label="I agree to the Terms of Service"
                                                checked={formData.termsOfService}
                                                onChange={checked => handleInputChange('termsOfService', checked)}
                                            />
                                            {errors.termsOfService && typeof errors.termsOfService === 'string' && (
                                                <span className={styles.errorText}>{errors.termsOfService}</span>
                                            )}
                                        </div>
                                        <div className={styles.checkboxField}>
                                            <Checkbox
                                                label="I agree to the Privacy Policy"
                                                checked={formData.privacyPolicy}
                                                onChange={checked => handleInputChange('privacyPolicy', checked)}
                                            />
                                            {errors.privacyPolicy && typeof errors.privacyPolicy === 'string' && (
                                                <span className={styles.errorText}>{errors.privacyPolicy}</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Action Button */}
                            <div className={styles.formActions}>
                                <Button
                                    label={isViewMode ? 'Close' : isSaving ? 'Saving...' : 'Save'}
                                    variant="black"
                                    size="large"
                                    onClick={isViewMode ? handleClose : handleSave}
                                    disabled={isSaving}
                                />
                            </div>
                        </form>
                    </div>
                </BaseModal.Content>
            </BaseModal>
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={resetFormAndClose}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}
        </>
    );
};

export default AddLocationModal;
